import React, { useState, useRef, useEffect, useMemo } from "react";
import { IBowser } from "bowser";
import { Image, ModelFiles } from "./model";
import { Video } from "../Store/models/Video";
import { ProductDisclaimer } from "~/ProductDetails/ProductDetailPageResult";
import ModalDisplay from "~/common/MediaViewerV2/ModalDisplay/ModalDisplay";
import FullViewDisplay from "~/common/MediaViewerV2/FullViewDisplay/FullViewDisplay";
import { useFeatureFlag } from "../contexts/FeatureFlagProvider";
import { useIsAugmentedRealitySupported } from "./FullViewDisplay/SeeIn3DButton/SeeIn3DButton";
import { isWebGlSupported } from "./utils";
import useIsMounted from "../useIsMounted";
export interface MediaViewerV2Props {
  videoTitle: string;
  alt: string;
  images: Image[];
  videos: Video[];
  modelFiles?: ModelFiles;
  productDisclaimer?: ProductDisclaimer;
  activeItemNumberIndex: number;
  productImageDisclaimer?: string;
  title: string;
  styles?: string;
}

// todo: rename this and the associated directory on completion of 1092256
const MediaViewerV2 = ({
  images,
  videos,
  modelFiles,
  productImageDisclaimer,
  activeItemNumberIndex,
  title = "",
  styles
}: MediaViewerV2Props) => {
  const placeholderImageUrl = "//www.webstaurantstore.com/uploads/design/2022/2/pdp-gallery-placeholder-white-600.png";
  const [isMobileScreenSize, setIsMobileScreenSize] = useState(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [isImageSelected, setIsImageSelected] = useState(true);
  const [bowser, setBowser] = useState<IBowser>();
  const slides = useRef<{
    [key: string]: HTMLDivElement;
  }>({});
  const thumbnails = useRef<HTMLDivElement[]>([]);
  const observer = useRef<IntersectionObserver | null>(null);
  const overlayIsOpen = useRef<boolean>(false);
  const {
    evaluateFlag
  } = useFeatureFlag();
  const productDimensionsIsEnabled = evaluateFlag("3d-model-product-dimensions");
  const [showAugmentedRealityButton, setShowAugmentedRealityButton] = useState(false);
  useIsAugmentedRealitySupported(modelFiles, setShowAugmentedRealityButton);
  const isMounted = useIsMounted();
  const media = useMemo(() => {
    const primaryImage = images.find(image => image.isPrimaryDetailImage);
    const sortedImages = [...(primaryImage ? [primaryImage] : []), ...images.filter(image => !image.isPrimaryDetailImage)];
    const content = [];
    const [firstImage, ...otherImages] = sortedImages;
    if (firstImage) {
      content.push(firstImage);
    }
    if (modelFiles && isWebGlSupported && (!isMounted || !showAugmentedRealityButton)) {
      // The AR button, if enabled, will use modelFiles directly, and we should not show any other 3D buttons.
      // Don't add modelFiles to content in that case.
      content.push(modelFiles);
    }
    return [...content, ...otherImages, ...videos];
  }, [images, modelFiles, videos, showAugmentedRealityButton, isMounted]);
  const handleSlide = (entries: IntersectionObserverEntry[]) => {
    if (overlayIsOpen.current) {
      return;
    }
    for (const entry of entries) {
      if (entry.intersectionRatio >= 0.75 && entry.intersectionRatio < 1) {
        const {
          target
        } = entry;
        if (!(target instanceof HTMLDivElement)) return;
        const {
          index
        } = target.dataset;
        if (index) {
          const selectedIndex = parseInt(index, 10);
          setSelectedMediaIndex(selectedIndex);
          thumbnails.current[selectedIndex]?.scrollIntoView({
            block: "nearest"
          });
        }
      }
    }
  };
  useEffect(() => {
    const loadIsMobile = async () => {
      const {
        default: isMobile
      } = await import("~/common/helpers/isMobile");
      setIsMobileScreenSize(isMobile("768px"));
    };
    loadIsMobile();
    const loadBowser = async () => {
      const bowser = await import("bowser");
      setBowser(bowser);
    };
    loadBowser();
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(handleSlide, {
      root: null,
      rootMargin: "0px",
      threshold: 0.75
    });
    Object.values(slides.current).forEach(slide => {
      if (slide) {
        observer.current?.observe(slide);
      }
    });
    return () => observer.current?.disconnect();
  }, []);
  useEffect(() => {
    setSelectedMediaIndex(0);
    slides.current[0]?.scrollIntoView({
      block: "nearest"
    });
  }, [activeItemNumberIndex]);
  const openZoom = () => {
    if (!isMobileScreenSize && overlayOpen) {
      setOverlayOpen(false);
    } else {
      setOverlayOpen(true);
    }
  };
  useEffect(() => {
    const mediaItem = media[selectedMediaIndex];
    if (mediaItem) {
      if ("isPrimaryDetailImage" in mediaItem) {
        setIsImageSelected(true);
      } else {
        setIsImageSelected(false);
      }
    }
  }, [media, selectedMediaIndex]);
  useEffect(() => {
    if (window && window.document) {
      overlayIsOpen.current = overlayOpen;
      const images = document.querySelectorAll("#GalleryImage, #FigureImage");
      images.forEach(el => {
        const imageObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            const lazyImage = entry.target as HTMLImageElement;
            const lazyFigure = entry.target as HTMLElement;
            if (entry.isIntersecting) {
              if (lazyImage.dataset.src) {
                lazyImage.src = lazyImage.dataset.src;
                imageObserver.unobserve(lazyImage);
              }
              if (lazyFigure.dataset.src && !overlayOpen) {
                lazyFigure.style.backgroundImage = lazyFigure.dataset.src;
                imageObserver.unobserve(lazyFigure);
              }
            } else {
              lazyImage.src = placeholderImageUrl;
              lazyFigure.style.backgroundImage = placeholderImageUrl;
            }
          });
        });
        imageObserver.observe(el);
      });
    }
  }, [overlayOpen, activeItemNumberIndex, media]);
  return <>
      <ModalDisplay openZoom={openZoom} overlayOpen={overlayOpen} setOverlayOpen={setOverlayOpen} isMobileScreenSize={isMobileScreenSize} media={media as []} selectedMediaIndex={selectedMediaIndex} setSelectedMediaIndex={setSelectedMediaIndex} title={title} placeholderImageUrl={placeholderImageUrl} bowser={bowser} slides={slides} productDimensionsIsEnabled={productDimensionsIsEnabled} data-sentry-element="ModalDisplay" data-sentry-source-file="index.tsx" />

      <FullViewDisplay media={media as []} selectedMediaIndex={selectedMediaIndex} activeItemNumberIndex={activeItemNumberIndex} slides={slides} setSelectedMediaIndex={setSelectedMediaIndex} thumbnails={thumbnails} title={title} openZoom={openZoom} overlayOpen={overlayOpen} isMobileScreenSize={isMobileScreenSize} placeholderImageUrl={placeholderImageUrl} productImageDisclaimer={productImageDisclaimer} isImageSelected={isImageSelected} modelFiles={modelFiles} bowser={bowser} productDimensionsIsEnabled={productDimensionsIsEnabled} containerClass={styles} showAugmentedRealityButton={showAugmentedRealityButton} data-sentry-element="FullViewDisplay" data-sentry-source-file="index.tsx" />
    </>;
};
export default MediaViewerV2;